<template>
  <div class="projects-management">
    <div class="container-fluid">
      <header class="page-header mb-4">
        <div class="row align-items-center">
          <div class="col-12 col-md-6 mb-3 mb-md-0">
            <h2 class="page-title mb-0">
              <a href="#" class="breadcrumb-link" @click.prevent="goToDashboard">Dashboard</a>
              <span class="breadcrumb-separator">&gt;</span>
              <span class="breadcrumb-current">Project Management</span>
            </h2>
          </div>

          <div class="col-12 col-md-6">
            <div class="header-actions d-flex gap-2 justify-content-md-end">
              <el-input
                v-model="searchQuery"
                class="search-input-v2"
                placeholder="Search project"
                @keyup="onSearchKey"
                clearable
              >
                <template #suffix>
                  <el-icon class="search-icon" @click="onSearch">
                    <Search />
                  </el-icon>
                </template>
              </el-input>

              <button class="btn btn-primary d-flex align-items-center" @click="openAddProject" :loading="addingProject">
                <el-icon><Plus /></el-icon>&nbsp;<span class="d-none d-sm-inline">Add Project</span>
              </button>          

              <button
                class="btn btn-primary d-flex align-items-center"
                @click="openManageCatalog"
                aria-label="Manage Catalog"
              >
                <span class="catalog-icon" aria-hidden>
                  <!-- simple grid/catalog SVG (4 squares) -->
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false" role="img" aria-hidden="true">
                    <rect x="3" y="3" width="7" height="7" rx="1" fill="currentColor" fill-opacity="0.9"/>
                    <rect x="14" y="3" width="7" height="7" rx="1" fill="currentColor" fill-opacity="0.9"/>
                    <rect x="3" y="14" width="7" height="7" rx="1" fill="currentColor" fill-opacity="0.9"/>
                    <rect x="14" y="14" width="7" height="7" rx="1" fill="currentColor" fill-opacity="0.9"/>
                  </svg>
                </span>
                &nbsp;<span class="d-none d-sm-inline">Manage Catalog</span>
              </button>
            </div>
          </div>
        </div>
      </header>
    
      <div v-if="loading" class="loading-state text-center py-5">
        <div class="spinner"></div>
        <p class="mt-3">Loading projects...</p>
      </div>

      <div v-if="error" class="alert alert-danger">{{ error }}</div>

      <div v-if="!loading && projects.length === 0 && !error" class="empty-list">
        No projects found.
      </div>

      <div class="row g-3 mb-4" v-if="projects.length > 0">
        <div
          class="col-12 col-md-6"
          v-for="proj in projects"
          :key="proj.projectId"         
          tabindex="0"                    
        >
        <div class="card project-card h-100">
          <div class="card-body d-flex flex-column">
            <!-- top row: badge, right aligned -->
            <div class="card-top-row mb-1">
              <div class="catalog-wrap">
                <span class="catalog-name" :title="proj.catalogName">{{ proj.catalogName }}</span>
              </div>

              <div style="margin-top: -16px;">
                <span :class="['badge', proj.disabled ? 'bg-secondary' : 'bg-success']">
                  {{ proj.disabled ? 'Disabled' : 'Published' }}
                </span>
              </div>
            </div>

            <!-- second row: title (full width, wrap when long) -->
            <div class="card-title-row mb-2">
              <h5 class="project-title mb-0" :title="proj.title">{{ proj.title }}</h5>
            </div>

            <div class="card-inner" style="padding-top: 6px; padding-bottom: 18px;">
              <div class="card-media d-flex align-items-center">
                <div class="card-thumb">
                  <div class="thumb-wrapper" style="position: relative; display: inline-block;">                    
                    <!-- Video thumbnail -->
                    <img
                      v-if="proj.isVideo && proj.hasImage"
                      :src="getProjectVideoThumbnail(proj)"
                      alt="video thumbnail"
                      class="news-thumbnail"                      
                    />
                    <!-- Image -->
                    <img
                      v-else-if="proj.hasImage && !imagesFailed.has(proj.projectId)"
                      :src="getProjectThumbnail(proj)"
                      alt="thumbnail"
                      class="news-thumbnail"                      
                    />

                    <!-- Last fallback placeholder -->
                    <img v-else src="@/assets/defaultError.png" alt="no-thumb" class="news-thumbnail" />

                    <!-- video badge, top left corner -->
                    <span v-if="proj.isVideo" class="video-badge" aria-hidden>
                      ▶
                    </span>
                  </div>
                </div>

                <div class="card-desc">
                  <p v-if="proj.description" class="project-desc mb-0" :title="proj.description">{{ proj.description }}</p>
                </div>
              </div>
            </div>

            <div class="card-actions" style="margin-left: -2px;">
              <div class="btn-group w-100" role="group">                
                <button class="btn btn-outline-info btn-sm" @click="previewProject(proj)" title="Preview">
                  <el-icon><View /></el-icon>
                </button>
                <button class="btn btn-outline-primary btn-sm" @click="editProject(proj)" title="Edit">
                  <el-icon><Edit /></el-icon>
                </button>
                <button 
                  class="btn btn-outline-secondary btn-sm" 
                   @click.stop="toggleProjectStatus(proj)" 
                  :title="proj.disabled ? 'Enable' : 'Disable'"
                >
                  <el-icon v-if="!proj.disabled"><Hide /></el-icon>
                  <el-icon v-else><Open /></el-icon>
                </button>                
                <button
                  class="btn btn-outline-warning btn-sm"
                  @click.stop="toggleProjectBest(proj)"
                  :title="proj.isBest ? 'Remove from homepage' : 'Set to homepage'"
                  :class="{ 'best-active': proj.isBest }"
                >
                  <el-icon v-if="proj.isBest"><StarFilled /></el-icon>
                  <el-icon v-else><Star /></el-icon>
                </button>
                <button class="btn btn-outline-danger btn-sm" @click.stop="deleteProject(proj)" title="Delete">
                  <el-icon><Delete /></el-icon>
                </button>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      <!-- Pagination -->
      <nav v-if="!loading && totalPages > 0" aria-label="Project pagination">     
        <ul class="pagination justify-content-center">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <button class="page-link" @click="currentPage--" :disabled="currentPage === 1">
              <div class="d-flex justify-content-start">
              <div style="padding-right: 1px; margin-top: 3px;"><el-icon><ArrowLeft/></el-icon></div>
              <div><span class="d-none d-sm-inline">Previous</span></div>
              </div>
            </button>
          </li>
          <li class="page-item disabled">
            <span class="page-link">Page {{ currentPage }} of {{ totalPages }}</span>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <button class="page-link" @click="currentPage++" :disabled="currentPage === totalPages">
              <div class="d-flex justify-content-start">
              <div><span class="d-none d-sm-inline">Next</span></div>
              <div style="padding-left: 1px; margin-top: 3px;"><el-icon><ArrowRight /></el-icon></div >
              </div>
            </button>
          </li>
        </ul>
      </nav>
    

    <!-- Add / Edit Project dialog (skeleton) -->
    <el-dialog
      :title="isEditMode ? 'Edit Project' : 'Add Project'"
      v-model="showAddProjectDialog"
      width="450px"
      :close-on-click-modal="false"
      center
    >
      <el-form :model="projectForm" ref="projectFormRef" :rules="projectRules" label-width="0px">
        <el-form-item prop="title">
          <el-input v-model="projectForm.title"  placeholder="Enter title"
              clearable/>
        </el-form-item>

        <el-form-item prop="catalogId">
          <el-select
            v-model="projectForm.catalogId"
            placeholder="Select catalog"
            clearable                        
          >
            <el-option
              v-for="cat in projectCatalogs"
              :key="cat.catalogId ?? cat.id ?? cat.value"
              :label="cat.catalogName ?? cat.name ?? cat.title"
              :value="cat.catalogId ?? cat.id ?? cat.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item prop="subtitle">
          <el-input
            type="textarea"
            :rows="5"
            v-model="projectForm.subtitle"
            placeholder="Enter description"
            maxlength="1000"
            show-word-limit
          />
        </el-form-item>              
        
        <!-- Media selector (collapse like News) -->
        <el-collapse v-model="mediaCollapseActive" class="image-collapse">
          <el-collapse-item name="media">
            <template #title>
              <div class="collapse-title">
                <el-icon><PictureFilled /></el-icon>
                <span style="margin-left:8px;">Upload Media</span>
              </div>
            </template>

            <div class="media-content">              
              <div style="margin-top:-8px; width:100%;">                
                  <el-form-item label-width="0px">
                    <el-radio-group v-model="mediaType" @change="onMediaTypeChange">
                      <el-radio-button label="image">Image</el-radio-button>
                      <el-radio-button label="video">Video</el-radio-button>
                    </el-radio-group>
                  </el-form-item>                
                <div v-if="mediaType === 'image'" class="upload-block" style="width:100%;">
                  <el-upload
                    class="news-image-uploader"
                    :auto-upload="false"
                    :show-file-list="false"
                    :on-change="handleProjectImageChange"
                    :before-upload="beforeProjectImageUpload"
                    accept="image/png"
                    :disabled="!!imagePreview"
                  >
                    <div v-if="imagePreview" class="image-preview-container" @click.stop>
                      <el-image
                        :src="imagePreview"
                        fit="cover"
                        class="preview-image"
                        :preview-src-list="[imagePreview]"
                        :z-index="3000"
                      >
                        <template #error>
                          <div class="image-error">
                            <el-icon><Picture /></el-icon>
                          </div>
                        </template>
                      </el-image>
                      <div class="image-actions">
                        <el-button size="small" type="danger" @click.stop="removeProjectImage" :icon="Delete">Remove</el-button>
                      </div>
                    </div>
                    <div v-else class="upload-placeholder">
                      <el-icon class="upload-icon"><Plus /></el-icon>
                      <div class="upload-hint">PNG (Max 3MB)</div>
                    </div>
                  </el-upload>
                </div>

                <div v-else class="upload-block">                  
                  <el-upload
                    class="news-image-uploader"
                    :auto-upload="false"
                    :show-file-list="false"
                    :on-change="handleProjectVideoChange"
                    :before-upload="beforeProjectVideoUpload"
                    accept="video/mp4"
                    :disabled="!!videoPreview"
                  >
                    <div v-if="videoPreview" class="image-preview-container" @click.stop>
                      <video :src="videoPreview" class="preview-video" controls muted playsinline style="width:100%; height:180px; object-fit:cover" />
                      <div class="image-actions">
                        <el-button size="small" type="danger" @click.stop="removeProjectVideo">Remove</el-button>
                      </div>
                    </div>
                    <div v-else class="upload-placeholder">
                      <el-icon class="upload-icon"><Plus /></el-icon>
                      <div class="upload-hint">Video File MP4</div>
                    </div>
                  </el-upload>
                  
                  
                </div>
              </div>
            </div>
          </el-collapse-item>
        </el-collapse>

        <el-form-item class="switch-row" style="padding: 0; padding-top: 16px;">
          <div class="switch-left" style="padding-left: 1px; padding-right: 100px;">
            <el-switch
              v-model="projectForm.isBest"
              active-text="Best"
              inactive-text="Normal"
            />
          </div>        
          <div class="switch-right">
            <el-switch
              v-model="projectEnabled"
              active-text="Enabled"
              inactive-text="Disabled"
            />
          </div>
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="closeAddProject">Cancel</el-button>
          <el-button type="primary" @click="submitProject" :loading="addingProject">
            {{ isEditMode ? 'Update' : 'Add' }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- Manage Catalog dialog (skeleton) -->
    <el-dialog title="Manage Catalog" v-model="showManageCatalogDialog" width="620px">
      <div style="min-height:120px">
        <p style="color:rgba(255,255,255,0.8)">Category management UI placeholder.</p>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="showManageCatalogDialog = false">Close</el-button>
        </div>
      </template>
    </el-dialog>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, computed, watch,getCurrentInstance } from 'vue';
import { PictureFilled } from '@element-plus/icons-vue';
import { useRouter, useRoute } from 'vue-router';
import apiClient from '@/apiClient';
import { ElMessage } from 'element-plus';

// router + route (we follow same search persistence pattern as AdminNewsListPage)
const router = useRouter();
const route = useRoute();
const { proxy } = getCurrentInstance();

// state
const projects = ref([]);
const projectCatalogs = ref([]);
const loading = ref(false);
const error = ref('');
const searchQuery = ref((route.query.search || '').toString());
const currentPage = ref(Number(route.query.page) || 1);
const pageSize = ref(8);
const projectsCount = ref(0);

const addingProject = ref(false);
const showAddProjectDialog = ref(false);
const isEditMode = ref(false);
const projectFormRef = ref(null);
const projectForm = ref({
  projectId: null,
  title: '',
  subtitle: '',
  catalogId: null,
  disabled: false,
  isBest: false
});

// Validation rules for Add/Edit Project (mirrors Add News rules style)
const projectRules = {
  catalogId: [
    { required: true, message: 'Please select a catalog', trigger: 'blur' }
  ],
  title: [
    { required: true, message: 'Please enter title', trigger: 'blur' },
    { max: 150, message: 'Title must be no more than 150 characters', trigger: 'blur' }
  ],
  subtitle: [
    { required: true, message: 'Please enter description', trigger: 'blur' },
    { max: 1000, message: 'Description cannot exceed 1000 characters', trigger: 'blur' }
  ],
  isBest: [
    {
      validator: (rule, value, callback) => {
        if (value) {
          // must be video and have both video + thumb uploaded (or existing previews)
          // if (mediaType.value !== 'video') return callback(new Error('To mark as Best, select Video as media type'));
          // if (!videoFile.value && !videoPreview.value) return callback(new Error('Please upload a video file to mark as Best'));
          // if (!videoThumbFile.value && !videoThumbPreview.value) return callback(new Error('Please upload a thumbnail for the video'));
        }
        callback();
      },
      trigger: 'change'
    }
  ]
};

// Media upload state
const mediaType = ref('image'); // 'image' or 'video'
// collapse state for media panel (matches News dialog behavior)
const mediaCollapseActive = ref([]); // ['media'] when expanded
const imageFile = ref(null);
const videoFile = ref(null);
const videoThumbFile = ref(null);
const imagePreview = ref('');
const videoPreview = ref('');
const videoThumbPreview = ref('');
// Tracks whether media (image/video) changed during an edit session
const mediaChanged = ref(false);
// Original media state when opening edit dialog
const originalMediaType = ref(null); // 'image' | 'video' | null
const originalHasMedia = ref(false);
const maxImageBytes = 3 * 1024 * 1024; // 3MB
const maxVideoBytes = 240 * 1024 * 1024; // 240MB


const projectEnabled = computed({
  get() {
    return !projectForm.value.disabled;
  },
  set(val) {
    projectForm.value.disabled = !val;
  }
});

const showManageCatalogDialog = ref(false);

// cache-busting map for thumbnails (per projectId)
const imageVersionMap = ref(new Map());
const imagesFailed = ref(new Set());

const totalPages = computed(() => Math.ceil(projectsCount.value / pageSize.value));

// watch for page changes
watch(currentPage, async () => {
  await loadProjectCatalogs();
  await loadProjects();  
});

function getProjectThumbnail(proj) {
  if (!proj || !proj.projectId) return '/placeholder.png';
  // If server signals hasImage === false, return placeholder
  if (proj.hasImage === false) return '/placeholder.png';
  let ver = imageVersionMap.value.get(proj.projectId);
  if (!ver) {
    ver = Date.now();
    imageVersionMap.value.set(proj.projectId, ver);
  }
  return `/projects/${proj.projectId}.png?v=${ver}&_=${Math.floor(Math.random() * 1000000)}`;
}


function getProjectVideoThumbnail(proj) {
  // return a server-side thumbnail path with cache-busting token
  const key = `${proj.projectId}-video`;
  let ver = imageVersionMap.value.get(key);
  if (!ver) {
    ver = Date.now();
    imageVersionMap.value.set(key, ver);
  }
  return `/projects/thumbs/${proj.projectId}.png?v=${ver}`;
}


// load projects (pageIndex optional)
async function loadProjects(pageIndex = currentPage.value) {
  loading.value = true;
  error.value = '';
  try {
    const req = {
      pageIndex,
      pageSize: pageSize.value,
      search: (route.query.search || searchQuery.value || '').toString()
    };

    // Example endpoint; adapt to your backend
    const resp = await apiClient.post('/Main/getAdminPaginatedProjectList', req);
    if (resp && resp.data) {
      const data = resp.data;
      projects.value = data.items || data.projects || [];
      projectsCount.value = data.total || data.totalCount || 0;
      // Keep current page in sync with returned page index if provided
      if (data.pageIndex) currentPage.value = data.pageIndex;
    } else {
      // fallback: empty
      projects.value = [];
      projectsCount.value = 0;
    }
  } catch (e) {
    error.value = (e?.response?.data?.msg) || 'Failed to load projects';
  } finally {
    loading.value = false;
  }
}

async function loadProjectCatalogs() {
  try {   
    const response = await apiClient.post('/Main/getExpProjectCatalogs');
    if (response && response.data && response.data.catalogs) {
      projectCatalogs.value = response.data.catalogs;      
    }
  } catch (e) {
    // Ignore catalog loading errors for now
  }
}

// search handlers (persist search in route.query same as news page)
async function onSearch() {
  currentPage.value = 1;
  const trimmed = (searchQuery.value || '').toString().trim();
  await router.replace({ query: { ...route.query, search: trimmed } }).catch(()=>{});
  await nextTick();
  await loadProjectCatalogs();
  await loadProjects(1);  
}

function onSearchKey(e) {
  if (!e) return;
  if (e.key === 'Enter') onSearch();
}


// actions
function openAddProject() {
  isEditMode.value = false;
  projectForm.value = {
    projectId: null,
    title: '',
    subtitle: '',
    catalogId: null,
    disabled: false,
    isBest: false
  };
  showAddProjectDialog.value = true;

  mediaType.value = 'image';
  // keep media panel collapsed by default for Add
  mediaCollapseActive.value = [];

  // clear any previously selected files
  imageFile.value = null;
  videoFile.value = null;
  videoThumbFile.value = null;
  imagePreview.value = '';
  videoPreview.value = '';
  videoThumbPreview.value = '';
  // reset media change tracking for add dialog
  mediaChanged.value = false;
  originalMediaType.value = null;
  originalHasMedia.value = false;

  if(projectFormRef.value) {
    projectFormRef.value.clearValidate();
  }
}

function closeAddProject() {
  showAddProjectDialog.value = false;
  _revokePreviews();
}

async function submitProject() {
  if (!projectFormRef.value) return;
  // validate form first
  try {
    await projectFormRef.value.validate();
  } catch (validateErr) {
    // validation failed; stop submission
    return;
  }
  addingProject.value = true;
  try {
    // Validate if you added rules; here we skip for brevity
    // Example endpoints:
    let resp;
    if (isEditMode.value && projectForm.value.projectId) {
      resp = await apiClient.post('/Main/editProject', {
        projectId: projectForm.value.projectId,
        hasMedia: mediaType.value === 'video' ? (videoFile.value ? true : false) : (imageFile.value ? true : false),
        isMediaChanged: mediaChanged.value,
        title: projectForm.value.title,
        description: projectForm.value.subtitle,
        catalogId: projectForm.value.catalogId,
        disabled: projectForm.value.disabled,
        isBest: projectForm.value.isBest,
        isVideo: mediaType.value === 'video'
      });
      if(resp && resp.data) {     
        proxy.$handleTips(resp.data.tag, resp.data.msg, error);
      }
    } else {
      resp = await apiClient.post('/Main/addNewProject', {
        title: projectForm.value.title,
        description: projectForm.value.subtitle,
        catalogId: projectForm.value.catalogId,
        disabled: projectForm.value.disabled,
        isBest: projectForm.value.isBest,
        isVideo: mediaType.value === 'video'
      });
      if(resp && resp.data) {     
        proxy.$handleTips(resp.data.tag, resp.data.msg, error);
      }
    }

    const savedId = (resp && resp.data && resp.data.projectId) || 0;

    // Upload image (Image mode)
    if (mediaType.value === 'image' && imageFile.value && savedId) {
      try {
        const fd = new FormData();
        fd.append('file', imageFile.value);
        fd.append('projectId', savedId);
        await apiClient.post('/Main/uploadProjectImage', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        imageVersionMap.value.set(savedId, Date.now());
      } catch (e) {
        const errorMsg = e?.response?.data?.msg || 'Image upload failed';
        proxy.$handleTips(e?.response?.data?.tag, errorMsg, e);
      }
    }

    // Upload video (Video mode)
    if (mediaType.value === 'video' && videoFile.value && savedId) {
      try {
        const fdv = new FormData();
        fdv.append('file', videoFile.value);
        fdv.append('projectId', savedId);
        await apiClient.post('/Main/uploadProjectVideo', fdv, { headers: { 'Content-Type': 'multipart/form-data' } });      
       
      } catch (e) {
        const errorMsg = e?.response?.data?.msg || 'Video upload failed';
        proxy.$handleTips(e?.response?.data?.tag, errorMsg, e);        
      }
    }

    showAddProjectDialog.value = false;
    // reset media change tracking after successful save
    mediaChanged.value = false;
    originalMediaType.value = null;
    originalHasMedia.value = false;
    await loadProjects(currentPage.value);
  } catch (e) {    
    const errorMsg = (e?.response?.data?.msg) || 'Failed to save project';
    proxy.$handleTips(e?.response?.data?.tag, errorMsg, e);
  } finally {
    addingProject.value = false;
  }
}

function editProject(proj) {
  if (!proj) return;
  isEditMode.value = true;
  projectForm.value = {
    projectId: proj.projectId,
    title: proj.title || '',
    subtitle: proj.description || '',
    catalogId: proj.catalogId ?? (proj.catalog?.id ?? null),
    disabled: proj.disabled,
    isBest: proj.isBest || false
  };
  // initialize media section
  mediaType.value = proj.isVideo ? 'video' : 'image';
  mediaCollapseActive.value = (proj.isVideo || proj.hasImage) ? ['media'] : [];
  // clear any previously selected files
  imageFile.value = null;
  videoFile.value = null;
  videoThumbFile.value = null;
  imagePreview.value = '';
  videoPreview.value = '';
  videoThumbPreview.value = '';
  // set previews based on existing project assets
  if (proj.isVideo) {
    // video preview (server path assumed)
    const key = `${proj.projectId}-video`;
    const ver = imageVersionMap.value.get(key) || Date.now();
    imageVersionMap.value.set(key, ver);
    videoPreview.value = `/projects/${proj.projectId}.mp4?v=${ver}`;
    videoThumbPreview.value = getProjectVideoThumbnail(proj);
    // mark existing media so submit logic can detect that media already exists
    videoFile.value = { existing: true };
    videoThumbFile.value = { existing: true };
  } else if (proj.hasImage) {
    imagePreview.value = getProjectThumbnail(proj);
    // mark existing image so submit logic can detect that image already exists
    imageFile.value = { existing: true };
  } else {
    // ensure no media markers remain
    imageFile.value = null;
    videoFile.value = null;
    videoThumbFile.value = null;
  }
  // record original media state for change detection
  originalMediaType.value = proj.isVideo ? 'video' : (proj.hasImage ? 'image' : null);
  originalHasMedia.value = !!(proj.isVideo || proj.hasImage);
  mediaChanged.value = false;
  showAddProjectDialog.value = true;

  if(projectFormRef.value) {
    projectFormRef.value.clearValidate();
  }
}

async function deleteProject(proj) {
  if (!proj) return;
  if (!confirm(`Are you sure you want to delete project "${proj.title}"?`)) return;
  try {
    const response = await apiClient.post('/Main/removeProject', { projectId: proj.projectId });
    if(response && response.data) {     
      proxy.$handleTips(response.data.tag, response.data.msg, error);
    }
   
    await loadProjects(currentPage.value);
  } catch (e) {
    const errorMsg = (e?.response?.data?.msg) || 'Failed to delete project';
    proxy.$handleTips(e?.response?.data?.tag, errorMsg, e);
  }
}

function previewProject(proj) {
  // open project preview route if exists
  if (!proj) return;
  const routeData = router.resolve({ name: 'ExperienceDetail', params: { projectId: proj.projectId, isLogin: true } });
  window.open(routeData.href, '_blank');
}

function openManageCatalog() {
  showManageCatalogDialog.value = true;
}

function goToDashboard() {
  router.push({ name: 'AdminDashboard' }).catch(() => {});
}


async function toggleProjectStatus(proj) {
  if (!proj) return;
  const action = proj.disabled ? 'enable' : 'disable';
  if (!confirm(`Are you sure you want to ${action} project "${proj.title}"?`)) return;
  try {
    
    const response = await apiClient.post('/Main/switchProjectStatus', {
      projectId: proj.projectId,
      disabled: !proj.disabled
    });

    if (response && response.data) {     
      proj.disabled = !proj.disabled;
      proxy.$handleTips(response.data.tag, response.data.msg, error);
      await loadProjects(currentPage.value);
    }
  } catch (e) {
    const errorMsg = (e?.response?.data?.msg) || 'Failed to update project status';
    proxy.$handleTips(e?.response?.data?.tag, errorMsg, e);
  }
}

async function toggleProjectBest(proj) {
  if (!proj) return;
  const action = proj.isBest ? 'remove from homepage' : 'set as homepage';
  if (!confirm(`Are you sure you want to ${action} for project "${proj.title}"?`)) return;
  try {
    const response = await apiClient.post('/Main/switchProjectIsBest', {
      projectId: proj.projectId,
      isBest: !proj.isBest
    });

    // update local state
    if(response && response.data) {     
      proj.isBest = !proj.isBest;
      proxy.$handleTips(response.data.tag, response.data.msg, error);
      // Refresh list to ensure consistency
      await loadProjects(currentPage.value);
    }
  } catch (e) {
    const errorMsg = (e?.response?.data?.msg) || 'Failed to update homepage status';
    proxy.$handleTips(e?.response?.data?.tag, errorMsg, e);
  }
}

// Media handlers and triggers
function onMediaTypeChange() {
  if (!mediaCollapseActive.value.includes('media')) mediaCollapseActive.value = ['media'];
  // If user switches media type during edit and original type differs, mark change
  if (isEditMode.value && originalMediaType.value && originalMediaType.value !== mediaType.value) {
    mediaChanged.value = true;
  }
}



// el-upload wrappers and before-upload validators
function beforeProjectImageUpload(file) {
  const isPng = file.type === 'image/png';
  const isLt3 = file.size <= maxImageBytes;
  if (!isPng) {
    ElMessage.error('Image must be PNG format!');
    return false;
  }
  if (!isLt3) {
    ElMessage.error('Image size cannot exceed 3MB!');
    return false;
  }
  return true;
}

function handleProjectImageChange(file) {
  const raw = file && file.raw ? file.raw : (file && file.file ? file.file : null);
  if (!raw) return;
  imageFile.value = raw;
  if (imagePreview.value && imagePreview.value.startsWith('blob:')) URL.revokeObjectURL(imagePreview.value);
  imagePreview.value = URL.createObjectURL(raw);
  // mark that media changed (new image selected)
  mediaChanged.value = true;
}

function beforeProjectVideoUpload(file) {
  const isMp4 = file.type === 'video/mp4';
  const isLt = file.size <= maxVideoBytes;
  if (!isMp4) {
    ElMessage.error('Video must be MP4 format!');
    return false;
  }
  if (!isLt) {
    ElMessage.error('Video file is too large');
    return false;
  }
  return true;
}

function handleProjectVideoChange(file) {
  const raw = file && file.raw ? file.raw : (file && file.file ? file.file : null);
  if (!raw) return;
  videoFile.value = raw;
  if (videoPreview.value && videoPreview.value.startsWith('blob:')) URL.revokeObjectURL(videoPreview.value);
  videoPreview.value = URL.createObjectURL(raw);
  // mark that media changed (new video selected)
  mediaChanged.value = true;
}

function removeProjectImage() {
  if (imagePreview.value && imagePreview.value.startsWith('blob:')) URL.revokeObjectURL(imagePreview.value);
  imagePreview.value = '';
  imageFile.value = null;
  // mark that media was removed/changed
  mediaChanged.value = true;
}

function removeProjectVideo() {
  if (videoPreview.value && videoPreview.value.startsWith('blob:')) URL.revokeObjectURL(videoPreview.value);
  videoPreview.value = '';
  videoFile.value = null;
  // mark that media was removed/changed
  mediaChanged.value = true;
}

// cleanup object URLs when dialog closes
function _revokePreviews() {
  if (imagePreview.value && imagePreview.value.startsWith('blob:')) URL.revokeObjectURL(imagePreview.value);
  if (videoPreview.value && videoPreview.value.startsWith('blob:')) URL.revokeObjectURL(videoPreview.value);
  if (videoThumbPreview.value && videoThumbPreview.value.startsWith('blob:')) URL.revokeObjectURL(videoThumbPreview.value);
  imagePreview.value = '';
  videoPreview.value = '';
  videoThumbPreview.value = '';
  imageFile.value = null;
  videoFile.value = null;
  videoThumbFile.value = null;
  // reset media change tracking
  mediaChanged.value = false;
  originalMediaType.value = null;
  originalHasMedia.value = false;
}

// override close to cleanup
// (removed duplicate closeAddProject - original was updated earlier to revoke previews)

onMounted(async () => {
  // sync local search from route if present
  if (route.query.search) searchQuery.value = route.query.search.toString();
  // ensure current page honors route.query.page if present
  if (route.query.page) currentPage.value = Number(route.query.page) || 1;
  await loadProjectCatalogs();
  await loadProjects(currentPage.value);
});
</script>

<style scoped>
.projects-management {
  padding: 24px 0;
  min-height: calc(100vh - 84px);
  color: #e6f0f2;
  /* match AdminNewsListPage background */
  background: linear-gradient(180deg, #071018 0%, rgba(7,16,24,0.6) 100%);
}

/* Header */
.page-title {
  color: #13c2c2;
  font-weight: 700;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.breadcrumb-link {
  color: #13c2c2;
  text-decoration: none;
  transition: color 0.2s ease;  
}

.breadcrumb-link:hover {
  color: #0fa6a6;
  text-decoration: underline;
}

.breadcrumb-separator {
  color: rgba(255,255,255,0.4);
  font-weight: 400;
  margin: 0 4px;
}

.breadcrumb-current {
  color: #fff;
  font-weight: 700;
}

/* Header actions */
.header-actions {
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
}

.header-actions .btn {
  width: auto;
  min-width: 120px;
  padding: 6px 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Search input sizing */
.search-input-v2 {
  width: 260px;
  min-width: 200px;
  max-width: 420px;
}


/* Card base - like News card but keep purple gradient color */
.project-card {
  position: relative;
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 10px 30px rgba(2,8,12,0.45);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  background: linear-gradient(180deg, rgba(156,39,176,0.15), rgba(123,31,162,0.08));
  border: 1px solid rgba(156,39,176,0.25);
  display: flex;
  flex-direction: column;
} 

/* hover */
.project-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 40px rgba(2,8,12,0.6);
  border-color: rgba(156,39,176,0.30);
} 

/* layout: top row for badge (full width), then header row for catalog+title */
.card-top-row {
  width: 100%;
  display: flex;
  justify-content: space-between; /* catalog left, badge right */
  align-items: center;
  position: relative;
  top: -23px;            /* = 3px - card-body padding-top(16px) */
  margin-bottom: 5px;
  padding: 0 8px;
  box-sizing: border-box;
}


.catalog-name {
  display: inline-block;
  color: #ffffff;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal; /* allow wrap */
  word-break: break-word;
  font-size: 1rem;
  margin-left: -10px;
}


/* Title row: full width and wrap if long */
.card-title-row {
  width: 100%;
  padding: 0 8px;
  box-sizing: border-box;
  margin-bottom: 8px;
}
.card-title-row .project-title {
  white-space: normal !important;
  overflow-wrap: anywhere;
  word-break: break-word;
  color: #fff;
  font-size: 1rem;
  font-weight: 500;
  margin-top: -28px;
  margin-left: -10px;
}

/* variants */
.published-badge {
  background: #13c2c2;
}
.disabled-badge {
  background: rgba(255,255,255,0.12);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.06);
}

/* header title should be full width and wrap when long */
.card-header .header-title,
.card-header .project-name,
.card-header .catalog-name {
  white-space: normal !important; /* allow wrapping */
  overflow-wrap: anywhere;
  word-break: break-word;
  display: inline;
}

/* slightly reduce size to match card look */
.card-header .catalog-name {
  font-weight: 600;
  color: #ffffff;
}

.card-header .project-name {
  font-weight: 600;
  color: #ffffff;
}

/* spacing tweaks */
.sep {
  opacity: 0.9;
  margin: 0 6px;
}

/* Card media: image left, description right (vertical center) */
.card-media {
  gap: 12px;
  align-items: center; /* center vertically */
}

/* keep thumbnail sizing, remove conflicting margins if any */
.card-thumb {
  flex: 0 0 auto;
  display: block;
}
.news-thumbnail {
  width: 140px;
  height: 100px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.04);
  background: rgba(6,18,28,0.6);
  margin: 0; /* remove earlier negative left margin so edges align with text */
}

/* description area: take remaining width and allow wrapping */
.card-desc {
  flex: 1 1 auto;
  min-width: 0; /* allow text truncation/wrapping within flex */
  color: rgba(255,255,255,0.9);
}

/* description text styling */
.project-desc {
  margin: 0;
  color: rgba(255,255,255,0.9);
  font-size: 0.95rem;
  line-height: 1.4;
  word-break: break-word;
  overflow-wrap: anywhere;
  white-space: normal;
}


.btn-outline-primary {
  color: #13c2c2;
  border-color: rgba(19,194,194,0.3);
}

.btn-outline-primary:hover {
  background: rgba(19,194,194,0.15);
  border-color: #13c2c2;
  color: #13c2c2;
}

.btn-outline-secondary {
  color: rgba(255,255,255,0.7);
  border-color: rgba(255,255,255,0.2);
}

.btn-outline-secondary:hover {
  background: rgba(255,255,255,0.1);
  border-color: rgba(255,255,255,0.3);
  color: #fff;
}

.btn-outline-danger {
  color: #ff8b8b;
  border-color: rgba(255,139,139,0.3);
}

.btn-outline-danger:hover {
  background: rgba(255,139,139,0.15);
  border-color: #ff8b8b;
  color: #ff6b6b;
}

.btn-outline-info {
  color: #5bc0de;
  border-color: rgba(91,192,222,0.3);
}

.btn-outline-info:hover {
  background: rgba(91,192,222,0.15);
  border-color: #5bc0de;
  color: #5bc0de;
}

.btn-outline-warning {
  color: #ffc107;
  border-color: rgba(255,193,7,0.25);
}

.btn-outline-warning:hover {
  background: rgba(255,193,7,0.08);
  border-color: #ffc107;
  color: #ffc107;
}

.best-active {
  background: rgba(255,193,7,0.12) !important;
  border-color: rgba(255,193,7,0.35) !important;
  color: #ffc107 !important;
}

.switch-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 12px;
  padding: 0 6px; 
  box-sizing: border-box;
}

.switch-left,
.switch-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}


/* Pagination */
.pagination .page-link {
  background: rgba(255,255,255,0.04);
  border-color: rgba(255,255,255,0.1);
  color: #e6fffb;
}

.pagination .page-link:hover {
  background: rgba(255,255,255,0.08);
  border-color: rgba(255,255,255,0.15);
  color: #fff;
}

.pagination .page-item.disabled .page-link {
  background: rgba(255,255,255,0.02);
  border-color: rgba(255,255,255,0.05);
  color: rgba(255,255,255,0.4);
}

/* loading spinner */
.loading-state {
  text-align: center;
  padding: 40px 0;
  color: rgba(255,255,255,0.75);
}
.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(255,255,255,0.08);
  border-top-color: #9c27b0;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
  margin: 0 auto 10px;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* empty list */
.empty-list {
  text-align: center;
  color: rgba(255,255,255,0.5);
  padding: 24px;
}

/* dialog footer */
.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.btn-primary {
  background: #13c2c2;
  border-color: #13c2c2;
  color: #081014;
  font-weight: 600;
}

.btn-primary:hover,
.btn-primary:focus {
  background: #0fa6a6;
  border-color: #0fa6a6;
  color: #081014;
}


.thumb-wrapper {
  position: relative;
  display: inline-block;
}

.video-badge {
  position: absolute;
  top: 6px;
  left: 6px;
  background: rgba(0,0,0,0.55);
  color: #fff;
  font-size: 12px;
  line-height: 1;
  padding: 2px 6px;
  border-radius: 4px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.35);
  pointer-events: none;
}

/* Image upload collapse (match News styles) */
.image-collapse {
  width: 100%;
  border: none;
  background: transparent;
}

.image-collapse :deep(.el-collapse-item__header) {
  background: transparent;
  border: none;
  padding: 8px 0;
  color: rgba(255,255,255,0.8);
  font-size: 14px;
  height: auto;
  line-height: 1.5;
  justify-content: flex-start;
}

.image-collapse :deep(.el-collapse-item__header:hover) {
  background: transparent;
  color: rgba(255,255,255,0.9);
}

.image-collapse :deep(.el-collapse-item__wrap) {
  background: transparent;
  border: none;
}

.image-collapse :deep(.el-collapse-item__content) {
  padding: 16px 0 0 0;
  color: #fff;
  display: flex;
  justify-content: center;
}

.image-collapse :deep(.el-collapse-item__arrow) {
  color: rgba(255,255,255,0.6);
  margin-left: 8px;
  order: 1;
}

.collapse-title {
  display: flex;
  align-items: center;
  gap: 8px;
  order: 0;
}

.collapse-title .el-icon {
  font-size: 18px;
  color: rgba(255,255,255,0.6);
  transition: color 0.3s ease;
}

/* Center the media content and radio group inside the collapse */
.media-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.media-content :deep(.el-form-item) {
  width: auto; /* allow radio group to size to content */
}
.media-content :deep(.el-radio-group) {
  display: flex;
  justify-content: center;
  gap: 8px;
}

/* ensure inner wrapper doesn't stretch full width and centers its children */
.media-content > div {
  width: auto !important;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* constrain upload-block so upload placeholders stay centered */
.media-content .upload-block {
  width: 100%;
  max-width: 360px;
  display: flex;
  justify-content: center;
}


.news-image-uploader {
  width: auto;
  margin: 0 auto;
  border: none;
  padding: 0;
  background: transparent;
}

.news-image-uploader :deep(.el-upload) {
  width: 100%;
  display: block;
}

.news-image-uploader.is-disabled :deep(.el-upload) {
  cursor: default !important;
}

.news-image-uploader :deep(.el-upload.is-disabled) {
  cursor: default !important;
}


.upload-placeholder {
  width: 180px;
  height: 120px;
  margin: 0 auto;
  border: 2px dashed rgba(255,255,255,0.2);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(0,0,0,0.12);
  object-fit: cover;
}

.upload-placeholder:hover {
  border-color: #13c2c2;
  background: rgba(19,194,194,0.05);
}

.upload-icon {
  font-size: 48px;
  color: rgba(255,255,255,0.5);
  margin-bottom: 12px;
}

/* preview images and video sizing to match thumbnail */
.preview-image {
  width: 180px;
  height: 120px;
  object-fit: cover;
  border-radius: 6px;
}
.preview-video {
  width: 180px !important;
  height: 120px !important;
  object-fit: cover;
  border-radius: 6px;
}

.upload-hint {
  color: rgba(255,255,255,0.5);
  font-size: 12px;
}


/* responsive */
@media (max-width: 576px) {
  .page-title {
    font-size: 1.25rem;
    gap: 6px;
  }

  .project-card {
    flex-direction: column;
    align-items: stretch;
  }

  .thumb-wrap {
    width: 100%;
    height: 160px;
  }

  .search-input-v2 {
    width: 100% !important;
    min-width: 0;
    max-width: 100%;
  }

  /* Mobile: stack header items vertically and make each full width */
  .header-actions {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 10px;
  }

  .header-actions .btn,
  .header-actions button,
  .header-actions .el-button {
    width: 100% !important;
    min-width: 0;
    display: inline-flex;
    justify-content: center;
    padding-left: 12px;
    padding-right: 12px;
  }

  .header-actions .search-input-v2,
  .header-actions .search-input-v2 .el-input__inner {
    width: 100% !important;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }
}
</style>